import React, { forwardRef } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Dropdown, Image, NavDropdown } from 'react-bootstrap';
import { FaUserCircle } from 'react-icons/fa';
import { v4 as uuid } from 'uuid';
import { useDispatch } from 'react-redux';
import ROUTES from '../../constants/routes';
import { isBooleanTrue, isStringNotEmpty, isListEmpty, isObjectEmpty } from '../../utils/objectUtils';
import { useAuth } from '../../redux/ducks/auth';
import { useOrchestrator } from '../../redux/ducks/orchestrator';
import { useCart } from '../../redux/ducks/cart';
import { setLead } from '../../redux/ducks/analytics';
import AuthService from '../../firebase/AuthService';
import QuickMenuNavDropdown from '../../layouts/marketing/navbars/QuickMenuNavDropdown';
import { RoleSwitcherModalMenuItem } from '../../layouts/RoleSwitcherModal';
import { DashboardTutorialMenuItem } from './DashboardTutorialMenuItem';
import {
	hasAffiliatePermissions,
	hasCoveredEntityPermissions,
	hasHelpStaffPermissions,
	hasMarketingManagerPermissions,
	hasPartnerPermissions,
	hasRepPermissions,
} from '../../utils/permissions';
import { getAffiliateName, getCoveredEntityName, getPartnerName } from '../../utils/entityUtils';
import { getStaffDashboardLabel } from '../../utils/staffDashboardUtils';
import styles from './DashboardPageHeader.module.css';
import BrandName from '../BrandName';

/**
 * Builds avatar initials from a user's first and last name.
 *
 * @param {string} firstName - User first name.
 * @param {string} lastName - User last name.
 * @returns {string} Avatar initials or fallback question mark.
 */
const getInitials = (firstName, lastName) => {
	const first = firstName?.charAt(0)?.toUpperCase() ?? '';
	const last = lastName?.charAt(0)?.toUpperCase() ?? '';
	const initials = `${first}${last}`;
	return isStringNotEmpty(initials) ? initials : '?';
};

/**
 * Dropdown toggle button for the V2 user avatar menu.
 *
 * @param {object} props
 * @param {(event: React.MouseEvent) => void} props.onClick - Dropdown toggle handler.
 * @param {string} props.firstName - User first name.
 * @param {string} props.lastName - User last name.
 * @param {string} props.photoURL - User avatar URL.
 * @param {React.Ref<HTMLButtonElement>} ref - Forwarded dropdown ref.
 * @returns {JSX.Element} Avatar toggle button.
 */
const AvatarToggle = forwardRef(({ onClick, firstName, lastName, photoURL }, ref) => (
	<button
		ref={ref}
		type="button"
		onClick={(e) => {
			e.preventDefault();
			onClick(e);
		}}
		className={styles.dashboardPageHeaderAvatar}
		aria-label="User profile menu"
		data-tour="account-menu"
	>
		{isStringNotEmpty(photoURL) ? (
			<Image src={photoURL} alt="" roundedCircle style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
		) : (
			<span className={styles.dashboardPageHeaderAvatarInitials}>
				{getInitials(firstName, lastName)}
			</span>
		)}
	</button>
));
AvatarToggle.displayName = 'AvatarToggle';

/**
 * Renders the dashboard header account menu with user identity, dashboard links,
 * role-specific menus, refresh actions, role switcher, and sign-out.
 *
 * @returns {JSX.Element|null} User avatar menu, or null when no auth user is loaded.
 */
export const UserAvatarMenu = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const dispatch = useDispatch();

	const { authContext, currentAuthUser, firstName, lastName, profile, resetAuth } = useAuth();
	const { coveredEntities, affiliates, partners, getOrchestrator } = useOrchestrator();
	const { clearCart } = useCart();

	if (isObjectEmpty(currentAuthUser)) return null;

	const displayName = isStringNotEmpty(currentAuthUser?.displayName)
		? currentAuthUser.displayName
		: [firstName, lastName].filter(isStringNotEmpty).join(' ');
	const email = isStringNotEmpty(currentAuthUser?.email) ? currentAuthUser.email : (isStringNotEmpty(profile?.email) ? profile.email : '');
	const photoURL = isStringNotEmpty(currentAuthUser?.photoURL) ? currentAuthUser.photoURL : null;

	const signOut = async () => {
		try {
			const success = await AuthService.doSignOut();
			if (isBooleanTrue(success)) {
				await resetAuth();
				await clearCart();
				await dispatch(setLead(null));
				setSearchParams((params) => {
					params.delete('lid');
					return params;
				}, { replace: true });
			}
		} catch (err) {
			console.log(err?.message);
		}
	};

	const renderCoveredEntityUserDropDown = () => {
		if (isListEmpty(authContext?.coveredEntityUsers)) return null;
		const children = [];
		authContext.coveredEntityUsers.forEach((coveredEntityUser) => {
			children.push({
				id: uuid(),
				header: true,
				header_text: getCoveredEntityName(coveredEntityUser.coveredEntityId, coveredEntities),
			});
			if (hasCoveredEntityPermissions(authContext, coveredEntityUser.coveredEntityId, ['ADMIN']) && isStringNotEmpty(coveredEntityUser.coveredEntityId)) {
				children.push({
					id: uuid(),
					menuitem: 'Covered Entity Dashboard',
					link: `${ROUTES.COVERED_ENTITY}/${coveredEntityUser.coveredEntityId}`,
				});
			}
			if (hasCoveredEntityPermissions(authContext, coveredEntityUser.coveredEntityId, ['CLINICIAN']) && isStringNotEmpty(coveredEntityUser.coveredEntityId) && isStringNotEmpty(coveredEntityUser.userId)) {
				children.push({
					id: uuid(),
					menuitem: 'Clinician Dashboard',
					link: `${ROUTES.LAB}/${coveredEntityUser.coveredEntityId}${ROUTES.CLINICIAN}/${coveredEntityUser.userId}`,
				});
			}
		});
		return (
			<QuickMenuNavDropdown
				item={{ id: uuid(), menuitem: 'My Covered Entities', children }}
				onClick={() => undefined}
			/>
		);
	};

	const renderAffiliateUserDropDown = () => {
		if (isListEmpty(authContext?.affiliateUsers)) return null;
		const children = [];
		authContext.affiliateUsers.forEach((affiliateUser) => {
			if (hasAffiliatePermissions(authContext, affiliateUser.affiliateId, ['MANAGER', 'ADMIN']) && isStringNotEmpty(affiliateUser.affiliateId)) {
				children.push({
					id: uuid(),
					menuitem: `${getAffiliateName(affiliateUser.affiliateId, affiliates)} Affiliate Dashboard`,
					link: `${ROUTES.AFFILIATE}/${affiliateUser.affiliateId}`,
				});
			}
		});
		if (isListEmpty(children)) return null;
		return (
			<QuickMenuNavDropdown
				item={{ id: uuid(), menuitem: 'My Affiliates', children }}
				onClick={() => undefined}
			/>
		);
	};

	const renderPartnerUserDropDown = () => {
		if (isListEmpty(authContext?.partnerUsers)) return null;
		const children = [];
		authContext.partnerUsers.forEach((partnerUser) => {
			if (hasPartnerPermissions(authContext, partnerUser.partnerId, ['MANAGER', 'ADMIN']) && isStringNotEmpty(partnerUser.partnerId)) {
				children.push({
					id: uuid(),
					menuitem: `${getPartnerName(partnerUser.partnerId, partners)} Partner Storefront Dashboard`,
					link: `${ROUTES.PARTNER}/${partnerUser.partnerId}`,
				});
			}
		});
		if (isListEmpty(children)) return null;
		return (
			<QuickMenuNavDropdown
				item={{ id: uuid(), menuitem: 'My Partner Storefronts', children }}
				onClick={() => undefined}
			/>
		);
	};

	return (
		<Dropdown align="end">
			<Dropdown.Toggle
				as={AvatarToggle}
				id="v2-avatar-menu-toggle"
				firstName={firstName}
				lastName={lastName}
				photoURL={photoURL}
			/>
			<Dropdown.Menu className="dashboard-dropdown dropdown-menu-end mt-2 py-0" align="end">
				<Dropdown.Header className="mt-3">
					<div className="d-flex">
						<div className="avatar avatar-md avatar-indicators avatar-online">
							{isStringNotEmpty(photoURL) ? (
								<Image alt="" src={photoURL} roundedCircle loading="lazy" />
							) : (
								<FaUserCircle size="40" className="rounded-circle" />
							)}
						</div>
						<div className="ms-3 lh-1">
							<h5 className="mb-1">{displayName}</h5>
							<p className="mb-0 text-muted">{email}</p>
						</div>
					</div>
				</Dropdown.Header>
				<Dropdown.Divider />
				<Dropdown.Item as={Link} to={{ pathname: ROUTES.ACCOUNT, search: searchParams.toString() }}>
					My <BrandName />&nbsp;Dashboard
				</Dropdown.Item>
				<Dropdown.Item as={Link} to={{ pathname: ROUTES.MY_MFA, search: searchParams.toString() }}>
					Multi-Factor Auth
				</Dropdown.Item>
				<DashboardTutorialMenuItem />

				{renderCoveredEntityUserDropDown()}
				{renderAffiliateUserDropDown()}
				{renderPartnerUserDropDown()}

				{(hasRepPermissions(authContext) || hasHelpStaffPermissions(authContext) || hasMarketingManagerPermissions(authContext)) ? (
					<NavDropdown.Item as={Link} to={{ pathname: ROUTES.ADMIN, search: searchParams.toString() }}>
						{getStaffDashboardLabel(authContext)}
					</NavDropdown.Item>
				) : null}
				{hasRepPermissions(authContext) && isBooleanTrue(authContext?.isSuperAdmin) ? (
					<NavDropdown.Item as={Link} to={{ pathname: ROUTES.REP_DASHBOARD_PREVIEW, search: searchParams.toString() }}>
						Rep Dashboard Preview
					</NavDropdown.Item>
				) : null}
				{hasRepPermissions(authContext) ? (
					<NavDropdown.Item onClick={() => { getOrchestrator(true); }}>
						Refresh Local Storage
					</NavDropdown.Item>
				) : null}
				{hasRepPermissions(authContext) ? <RoleSwitcherModalMenuItem /> : null}

				<Dropdown.Divider />
				<Dropdown.Item onClick={(e) => { e.preventDefault(); signOut(); }} className="mb-3">
					<i className="fe fe-power me-2"></i> Sign Out
				</Dropdown.Item>
			</Dropdown.Menu>
		</Dropdown>
	);
};
